import type { Dispatch } from 'react';
import SelectDropDown from '@/components/UI/SelectDropDown';
import Select from '@/components/UI/Select';
import MenuContainer from '@/components/UI/SelectDropDown/Menus/MenuContainer';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { ChangeOption, GraphAction, GraphsState } from './Graph.model';
import type { SelectOption } from '@/components/UI/Select/Select';
import { AnalysisIssueCounter } from '@/components/Studies/Groups/Analysis/AnalysisIssueCounter.tsx';
import { type AnalysisIssue } from '@/model/Analysis.model.ts';
import { useModalAction } from '@/utils/modal.tsx';

interface GraphChangeOption {
  label: string;
  value: string;
}

interface ChangeOptionsLookup {
  absolute: GraphChangeOption;
  relative: GraphChangeOption;
  change: GraphChangeOption;
  cumulative: GraphChangeOption;
}

interface ToolbarProps {
  calculations: Array<PresetCalculation>;
  state: GraphsState;
  dispatch: Dispatch<GraphAction>;
  disabled?: { metrics: boolean; relative: boolean };
  issues?: Array<AnalysisIssue>;
}

const Toolbar: React.FC<ToolbarProps> = ({ calculations, state, dispatch, disabled, issues }) => {
  const { activeMetric, groups, changeOption } = state;
  const { openModal } = useModalAction();

  const changeOptionsLookup: ChangeOptionsLookup = {
    absolute: {
      label: 'Absolute',
      value: 'absolute',
    },
    relative: {
      label: 'Relative',
      value: 'relative',
    },
    change: {
      label: '% Change',
      value: 'change',
    },
    cumulative: {
      label: 'Cumulative',
      value: 'cumulative',
    },
  };
  const relativeOptions = Object.values(changeOptionsLookup);

  return (
    <div className="ph4 pv3 bg-white bb b--moon-gray flex items-center justify-between flex-wrap z-999">
      <div className="flex">
        <div data-testid="graphs__metric-select">
          <Select
            className="mr3"
            disabled={disabled?.metrics}
            defaultValue={{
              label: activeMetric?.name,
              value: activeMetric,
            }}
            options={calculations.map((c) => ({
              value: c,
              label: c.name,
            }))}
            isMulti={false}
            onChange={(value) =>
              dispatch({ type: 'setActiveMetric', data: (value as SelectOption<PresetCalculation>).value })
            }
          />
        </div>
        <SelectDropDown title="Groups" className="plain" alignMenu="left" testId="graphs__group-select">
          <MenuContainer style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <div className="ph3 pv2" data-testid="graphs__group-select--options">
              {groups.map(({ id, checked, name }, index) => (
                <Checkbox
                  key={id}
                  checked={checked}
                  onChange={() => {
                    const updatedGroups = [...groups];
                    updatedGroups[index].checked = !checked;
                    dispatch({
                      type: 'setGroups',
                      data: updatedGroups,
                    });
                  }}
                  id={id.toString()}
                  name={name}
                  label={name}
                  className="mv3"
                />
              ))}
            </div>
          </MenuContainer>
        </SelectDropDown>
      </div>
      <div className="flex items-center">
        <div className="mr3">
          <AnalysisIssueCounter
            issues={issues ?? []}
            onClick={() =>
              openModal('GRAPH_ISSUES', {
                issues: issues ?? [],
              })
            }
          />
        </div>
        <div data-testid="graphs__change-select">
          <Select
            defaultValue={changeOptionsLookup[changeOption]}
            options={relativeOptions}
            onChange={(value) =>
              dispatch({ type: 'setChangeOption', data: (value as SelectOption<ChangeOption>).value })
            }
            isMulti={false}
            disabled={disabled?.relative}
          />
        </div>
      </div>
    </div>
  );
};

export default Toolbar;
